/**
 * PublicOrGuarded — rota que existe para quem NÃO tem conta, sem abrir buraco
 * no gate do beta.
 *
 * Nasceu da abertura de SEO orgânico (06/08): `/livro/:workSlug` precisa ser
 * indexável, e toda rota de conteúdo estava em `Guarded`, que redireciona pro
 * `/login` antes do componente montar.
 *
 * ⚠️ A parte que NÃO é obvia, e é a razão deste arquivo existir em vez de um
 * simples "tira o Guarded": o `BetaAccessGate` só existe DENTRO do
 * `ProtectedRoute` (ver `AppRoutes.Guarded`). Uma rota sem guard nenhum
 * deixaria passar quem está **logado e fora da allowlist** — e como o
 * `postLoginTarget` devolve a pessoa exatamente pra cá depois do login
 * (`/livro/*` está na lista-branca `SHAREABLE_TARGET`), o caminho
 * "link do Google → ação → login → volta" entregaria uma sessão dentro do
 * produto sem passar pelo porteiro. `addBook` escreve em `user_books`, que as
 * rules liberam pro dono: a pessoa entraria na estante sem estar no beta.
 *
 * Então a regra é: **sem sessão, conteúdo público; com sessão, a cadeia
 * completa.** Quem já está dentro não muda de comportamento — o
 * `ProtectedRoute` é satisfeito trivialmente quando `user` existe, e o gate e
 * o onboarding seguem valendo.
 *
 * `redirectPending` entra na espera junto de `isLoading` pela mesma razão do
 * `RootEntry` (BUG-30): na volta do login Google por redirect existe uma
 * janela em que a sessão ainda não materializou. Sem essa espera, a versão
 * ANÔNIMA da página piscaria no fim do login de quem acabou de entrar.
 *
 * ⚠️ O modo público exige `hasPublicPayload()`, e isso NÃO é preciosismo: as
 * `firestore.rules` continuam fechadas (decisão de 06/08 — o dado público vem
 * pronto do servidor, não de rules abertas). Sem o payload, um visitante
 * anônimo chegaria aqui e teria a leitura do catálogo NEGADA, renderizando
 * "livro não encontrado" em vez do portão honesto de hoje. O gate por presença
 * faz esta fatia ser inerte até a function entrar no ar — a ordem de deploy
 * deixa de poder machucar. Ver `publicPayload.ts`.
 */

import { useEffect, type ReactElement } from "react";
import { useAuth } from "../hooks/useAuth";
import { prefetchLoginChunk } from "./routePrefetch";
import { ProtectedRoute } from "./ProtectedRoute";
import { OnboardingGuard } from "./OnboardingGuard";
import { BetaAccessGate } from "./BetaAccessGate";
import { PageLoader } from "./PageLoader";
import { hasPublicPayload } from "./publicPayload";

export function PublicOrGuarded({ children }: { children: ReactElement }) {
  const { user, isAnonymous, isLoading, redirectPending } = useAuth();

  // O chunk da página baixa ENQUANTO a sessão resolve, não depois (17/09).
  // Sem sessão o destino é a própria página pública (Login, na prática);
  // com sessão, o `ProtectedRoute` abaixo já pré-carrega a Home.
  useEffect(() => {
    if (isLoading) prefetchLoginChunk();
  }, [isLoading]);

  if (isLoading || redirectPending) return <PageLoader />;

  // Visitante sem conta E com dado servido pelo servidor: a página renderiza em
  // modo leitura e manda toda ação pro `/login` (ver `useRequireAuth`).
  //
  // A sessão anônima do perfil público (F1.5, 11/09) conta como "sem conta"
  // aqui: se ela navegar pra `/livro/...`, o comportamento é o mesmo de um
  // visitante que nunca logou — nenhuma rota trata sessão anônima como conta.
  if ((!user || isAnonymous) && hasPublicPayload()) return children;

  return (
    <ProtectedRoute>
      <BetaAccessGate>
        <OnboardingGuard>{children}</OnboardingGuard>
      </BetaAccessGate>
    </ProtectedRoute>
  );
}
